import { Link, useRouterState } from "@tanstack/react-router";
import { Activity, Box, Boxes, CalendarClock, CreditCard, FileText, Home, Radio, ScrollText, Settings, Users } from "lucide-react";
import { cn } from "@/lib/cn";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { WorkspaceSwitcher } from "./workspace-switcher";
import { PlanUsage } from "./plan-usage";
import { UserMenu } from "./user-menu";
import { useMeta } from "@/stores/meta";

type NavItem = { to: string; label: string; icon: React.ElementType; cloudOnly?: boolean };
type NavGroup = { label: string; items: NavItem[] };

const groups: NavGroup[] = [
  {
    label: "Monitor",
    items: [
      { to: "/", label: "Overview", icon: Home },
      { to: "/stream", label: "Stream", icon: Radio },
      { to: "/events", label: "Events", icon: Activity },
      { to: "/runs", label: "Runs", icon: FileText },
      { to: "/schedules", label: "Schedules", icon: CalendarClock },
    ],
  },
  {
    label: "Build",
    items: [
      { to: "/functions", label: "Functions", icon: Box },
      { to: "/apps", label: "Apps", icon: Boxes },
    ],
  },
  {
    label: "Security",
    items: [{ to: "/audit", label: "Audit log", icon: ScrollText }],
  },
  {
    label: "Account",
    items: [
      { to: "/team", label: "Team", icon: Users },
      // Billing (plans, upgrade, spend cap) only exists on Deleggate Cloud —
      // self-hosted has no billing, so hide it there.
      { to: "/billing", label: "Billing", icon: CreditCard, cloudOnly: true },
      { to: "/settings", label: "Settings", icon: Settings },
    ],
  },
];

function isActive(pathname: string, to: string) {
  return pathname === to || (to !== "/" && pathname.startsWith(to));
}

export function AppSidebar() {
  const { location } = useRouterState();
  // Cloud-only nav items (Billing) are hidden until we confirm this is the cloud
  // tier, so a self-hosted instance never shows a dead billing link.
  const cloud = useMeta((s) => s.meta?.cloud ?? false);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <WorkspaceSwitcher />
      </SidebarHeader>

      <SidebarContent>
        {groups.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel className="font-mono text-[10px] uppercase tracking-[0.14em]">
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.filter((it) => !it.cloudOnly || cloud).map((it) => {
                  const Icon = it.icon;
                  const active = isActive(location.pathname, it.to);
                  return (
                    <SidebarMenuItem key={it.to}>
                      <SidebarMenuButton asChild isActive={active} tooltip={it.label}>
                        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                        <Link to={it.to as any}>
                          <Icon className={cn(active ? "text-brand" : "")} />
                          <span>{it.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>

      <SidebarFooter>
        {/* The plan/quota widget is meaningless on self-host (no plan, unlimited),
            so it only shows on cloud — same reasoning as hiding Billing. */}
        {cloud && <PlanUsage />}
        <UserMenu />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
